import React, {CSSProperties, ReactNode, useState} from "react";
import {FormControl, FormHelperText} from "@mui/material";
import {LoadingButton, LoadingButtonProps} from "@mui/lab";
import {Save} from "@mui/icons-material";

export interface ProcessButtonProps {
	readonly resetKeys?: any;
	readonly defaultText: ReactNode;
	readonly completedText: ReactNode;
	readonly inProgressText: ReactNode;
	readonly onClick?: () => Promise<any>;
	readonly startIcon?: React.ReactNode;
	readonly defaultColor?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
	
	readonly variant?: "text" | "outlined" | "contained";
	
	readonly buttonProps?: LoadingButtonProps;
	
	readonly style?: CSSProperties;
}

/**
 * creates a button with an async processing option, providing feedback in the component itself
 */
export function ProcessButton(props: ProcessButtonProps) {
	return <span key={JSON.stringify(props.resetKeys)}>
		<ProcessButtonInner {...props}/>
	</span>;
}

/**
 * actually creates the button
 * the above function is a wrapper state layer that allows resetting the state of the inner component
 * that way, a change in the resetKeys property will reset the state of the ProcessButton
 */
function ProcessButtonInner(props: ProcessButtonProps) {
	const [state, setState] = useState<"default" | "in-progress" | "completed" | "error">("default");
	const [error, setError] = useState<string>();
	
	const text = state === "in-progress"
		? props.inProgressText
		: state === "completed"
			? props.completedText
			: props.defaultText;
	
	return <FormControl error={!!error}>
		<LoadingButton
			{...props.buttonProps}
			loading={state === "in-progress"}
			loadingPosition={"start"}
			startIcon={props.startIcon ?? <Save/>}
			color={state === "error" ? "error" : state === "completed" ? "success" : props.defaultColor}
			variant={props.variant}
			// todo allow {...props}
			onClick={async () => {
				if (props.onClick) {
					setState("in-progress");
					setError(undefined);
					try {
						await props.onClick();
						setState("completed");
					} catch (ex) {
						setState("error");
						setError(ex?.toString() ?? "error");
					}
				}
			}}
			style={props.style}
		>{text}</LoadingButton>
		<FormHelperText>{error}</FormHelperText>
	</FormControl>;
}
